import { Component, createRef } from "react";
import { connect } from "react-redux";
import { type Field, Form, type InjectedFormProps, type Validator, reduxForm } from "redux-form";

import { Callout, CalloutIntent } from "widgets";

import { getFormError, getFormWarning, getSelectedOption, handleTabInForm } from "../../../app-utils/form.js";
import {
  type Attribute,
  type ExportWorkflowField,
  type SampleType,
  type Workflow,
  type WorkflowExportMapping,
} from "../../../graphql.js";
import { type IPlaybookConfigState, updatePlaybookConfigState } from "../../../redux/modules/playbook-admin.js";
import { type IAppState } from "../../../redux/reducer.js";
import { ATTRIBUTE_TYPE, COLUMN_SET } from "../../../settings/settings.js";
import TabFence from "../../core/forms/TabFence.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import ExportedWorkflowFieldMapper, {
  ExportedWorkflowFieldMapperReduxFormField,
} from "./ExportedWorkflowFieldMapper.js";

type IPlaybookConfigNgsResultsScreenOwnProps = IPlaybookWizardStepProvidedProps;

interface IPlaybookConfigNgsResultsScreenAppStateProps {
  globalFormError: string;
  globalFormWarning: string;
  initialValues: Partial<FormData>;
  sampleTypeAttributes: Attribute[];
  selectedWorkflow: Workflow;
}

interface IPlaybookConfigNgsResultsScreenDispatchProps {
  updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => void;
}

export interface IPlaybookConfigNgsResultsScreenProps
  extends IPlaybookConfigNgsResultsScreenAppStateProps,
    IPlaybookConfigNgsResultsScreenDispatchProps,
    IPlaybookConfigNgsResultsScreenOwnProps,
    InjectedFormProps<FormData, any>,
    IPlaybookWizardStepInjectedProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: IPlaybookConfigNgsResultsScreenOwnProps,
): IPlaybookConfigNgsResultsScreenAppStateProps {
  const playbookFormState: Partial<IPlaybookConfigState> = state.playbookAdminState.playbookConfigState;
  const selectedWorkflow: Workflow = state.listing.approvedWorkflows.find(
    (workflow) => workflow.sourceId === state.playbookAdminState.playbookConfigState.workflowId,
  );
  const sampleType: SampleType = state.sampleAdmin.sampleType;

  const sampleAttributes: Attribute[] = sampleType.sampleAttributes.filter(
    (a: Attribute) => a.columnSet?.id === COLUMN_SET.COS_ANALYTICAL_ID.id,
  ); // only analytical attributes can be mapped (versus annotation data for form input)

  return {
    globalFormError: getFormError(PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_NAME, state),
    globalFormWarning: getFormWarning(PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_NAME, state),
    initialValues: {
      [PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS]:
        playbookFormState.workflowExportMappings
          ?.map((mapping: WorkflowExportMapping) => ({
            [PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD]:
              getSelectedOption<ExportWorkflowField>(
                selectedWorkflow.exportedFields,
                mapping.exportedWorkflowFieldName,
                (option: ExportWorkflowField) => option.name,
              ),
            [PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE]:
              getSelectedOption<Attribute>(
                sampleAttributes,
                mapping.sampleAttributeId,
                (option: Attribute) => option.id,
              ),
          }))
          // make sure the workflow field still exists, otherwise we remove it from the mapping
          .filter(
            (field) => !!field[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD],
          ),
    },
    sampleTypeAttributes: sampleAttributes,
    selectedWorkflow: selectedWorkflow,
  };
}

function mapDispatchToProps(dispatch): IPlaybookConfigNgsResultsScreenDispatchProps {
  return {
    updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => dispatch(updatePlaybookConfigState(state)),
  };
}

class PlaybookConfigNgsResultsScreenImpl extends Component<IPlaybookConfigNgsResultsScreenProps> {
  public static PLAYBOOK_CREATION_FORM_NAME = "PLAYBOOK_CREATION_NGS_RESULTS";
  // field identifiers
  public static PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS = "PLAYBOOK_CREATION_NGS_RESULTS_MAPPING";
  public static PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD =
    "PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD";
  public static PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE = "PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE";

  private lastTabRef = createRef<Field>();
  private firstTabRef = createRef<Field>();

  constructor(props) {
    super(props);
  }

  handleSubmitData = (values) => {
    const mappings = values[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS];
    const mappingsPayload: WorkflowExportMapping[] = mappings
      ? mappings.map((mapping, _index) => {
          const workflowField: ExportWorkflowField =
            mapping[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD];
          const attribute: Attribute =
            mapping[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE];
          const mappingPayload: WorkflowExportMapping = {
            exportedWorkflowFieldName: workflowField.name,
            sampleAttributeId: attribute.id,
          };
          return mappingPayload;
        })
      : [];
    const playbookCreationState: Partial<IPlaybookConfigState> = {
      workflowExportMappings: mappingsPayload,
    };
    this.props.updatePlaybookCreationState(playbookCreationState);

    // at this stage the validation has succeeded
    this.props.proceedWithNextStep();
  };

  render() {
    return (
      <PlaybookWizardStep
        {...this.props}
        wizardFooterData={{
          error: this.props.globalFormError || this.props.error,
          warning: this.props.warning,
          nextButtonProps: {
            label: "Next",
            isLoading: !!this.props.asyncValidating,
            onPress: this.props.handleSubmit(this.handleSubmitData),
          },
        }}
      >
        <Form className="form-horizontal ngs-data-screen" onSubmit={this.props.handleSubmit(this.handleSubmitData)}>
          <Callout intent={CalloutIntent.Warning}>
            Loss of precision is possible when numerical values are saved to the sample database
          </Callout>

          <div onKeyDownCapture={handleTabInForm}>
            <TabFence targetRef={this.lastTabRef} />

            <ExportedWorkflowFieldMapperReduxFormField
              name={PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS}
              component={ExportedWorkflowFieldMapper}
              availableExportFields={this.props.selectedWorkflow.exportedFields}
              isLoadingAttributes={false}
              sampleAttributes={this.props.sampleTypeAttributes}
              validate={PlaybookConfigNgsResultsScreenImpl.workflowFieldMappingValidator}
            />

            <TabFence targetRef={this.firstTabRef} />
          </div>
        </Form>
      </PlaybookWizardStep>
    );
  }

  static workflowFieldMappingValidator: Validator = (mappings): string | undefined => {
    if (mappings?.length) {
      const attributeIds: number[] = mappings
        .filter(
          (mapping) => mapping[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE],
        )
        .map((mapping) => {
          const attribute: Attribute =
            mapping[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE];
          return attribute.id;
        });
      const hasDuplicates = attributeIds.some((attributeId, idx) => attributeIds.indexOf(attributeId) != idx);

      if (hasDuplicates) {
        return "Multiple workflow fields are mapped to the same sample attribute";
      }
    }
    return undefined;
  };

  // currently not in use, this is for showing a warning in the footer (keep it for reference)
  static warn = (values) => {
    const mappings = values[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS];

    if (mappings?.length) {
      const numericalAttributes: Attribute[] = mappings.find((mapping) => {
        const attribute: Attribute =
          mapping[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE];
        return attribute?.attributeType.id === ATTRIBUTE_TYPE.FREE_NUMERICAL;
      });
      if (numericalAttributes) {
        return { _warning: "Loss of precision is possible when numerical values are saved to the sample database" };
      }
    }

    return {};
  };
}

const PlaybookConfigNgsResultsScreen = connect<
  IPlaybookConfigNgsResultsScreenAppStateProps,
  IPlaybookConfigNgsResultsScreenDispatchProps,
  IPlaybookConfigNgsResultsScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_NAME,
    //warn: PlaybookCreationNgsResultsScreenImpl.warn,
    destroyOnUnmount: true,
  })(PlaybookConfigNgsResultsScreenImpl) as any,
);

export { PlaybookConfigNgsResultsScreenImpl };
export default PlaybookConfigNgsResultsScreen;
